import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { 
  Play, Loader2, AlertCircle, CheckCircle, Copy, 
  Terminal, Code, ChevronDown, ChevronRight, Clock,
  Zap, FileJson
} from 'lucide-react';
import { Card, Badge, CodeEditor } from '../../components/ui';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

// Types
interface ProviderInstance {
  id: string;
  name: string;
  templateId: string;
  template?: {
    name: string;
    type: string;
    operations: Operation[];
  };
}

interface Operation {
  name: string;
  description?: string;
  method: string;
  endpoint: string;
  parameters?: OperationParameter[];
}

interface OperationParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  required: boolean;
  description?: string;
  defaultValue?: any;
}

interface ExecutionResult {
  success: boolean;
  statusCode?: number;
  duration?: number;
  request?: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body?: any;
  };
  response?: {
    headers: Record<string, string>;
    body: any;
  };
  error?: {
    message: string;
    code?: string;
    details?: any;
  };
  curlCommand?: string;
}

interface TestConsoleProps {
  instanceId?: string;
  className?: string;
}

export const TestConsole: React.FC<TestConsoleProps> = ({ instanceId: propInstanceId, className }) => {
  const [searchParams] = useSearchParams();
  const urlInstanceId = searchParams.get('instanceId');
  const initialInstanceId = propInstanceId || urlInstanceId || '';
  
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>(initialInstanceId);
  const [selectedOperation, setSelectedOperation] = useState<string>('');
  const [parameters, setParameters] = useState<Record<string, any>>({});
  const [result, setResult] = useState<ExecutionResult | null>(null);
  const [showRequest, setShowRequest] = useState(true);
  const [showResponse, setShowResponse] = useState(true);
  const [isDryRun, setIsDryRun] = useState(false);

  // Load instances
  const { data: instances = [], isLoading: loadingInstances } = useQuery({
    queryKey: ['provider-instances'],
    queryFn: async () => {
      const response = await api.get('/provider-instances');
      // Backend returns { data: [], total: number }
      const result = response.data;
      return result.data || result.instances || result || [];
    },
  });

  // Get selected instance
  const selectedInstance = instances.find((i: ProviderInstance) => i.id === selectedInstanceId);
  const operations: Operation[] = selectedInstance?.template?.operations || [];
  const currentOperation = operations.find((op: Operation) => op.name === selectedOperation);

  // Update selected instance when prop or URL param changes
  useEffect(() => {
    const newInstanceId = propInstanceId || urlInstanceId;
    if (newInstanceId) {
      setSelectedInstanceId(newInstanceId);
    }
  }, [propInstanceId, urlInstanceId]);

  // Reset parameters when operation changes
  useEffect(() => {
    if (currentOperation?.parameters) {
      const defaultParams: Record<string, any> = {};
      currentOperation.parameters.forEach((param: OperationParameter) => {
        if (param.defaultValue !== undefined) {
          defaultParams[param.name] = param.defaultValue;
        }
      });
      setParameters(defaultParams);
    } else {
      setParameters({});
    }
    setResult(null);
  }, [selectedOperation, currentOperation]);

  // Execute mutation
  const executeMutation = useMutation({
    mutationFn: async ({ dryRun }: { dryRun: boolean }) => {
      // Use the correct backend endpoint
      const endpoint = dryRun 
        ? `/provider-test/instance/${selectedInstanceId}/dry-run`
        : `/provider-test/instance/${selectedInstanceId}/test`;
      
      const response = await api.post(endpoint, {
        operationName: selectedOperation,
        inputData: parameters,
      });
      return response.data;
    },
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (error: any) => {
      setResult({
        success: false,
        error: {
          message: error.response?.data?.message || 'Erro ao executar operação',
          details: error.response?.data,
        },
      });
    },
  });

  // Handle parameter change
  const handleParameterChange = (name: string, value: any) => {
    setParameters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle execute
  const handleExecute = (dryRun: boolean) => {
    setIsDryRun(dryRun);
    executeMutation.mutate({ dryRun });
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Format JSON
  const formatJson = (obj: any): string => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <div className={cn('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center gap-3">
        <Terminal className="w-6 h-6 text-primary-400" />
        <div>
          <h2 className="text-xl font-bold text-white">Console de Testes</h2>
          <p className="text-sm text-surface-400">
            Execute operações em modo sandbox para testar integrações
          </p>
        </div>
      </div>

      {/* Instance & Operation Selection */}
      <Card className="p-4">
        <div className="grid grid-cols-2 gap-4">
          {/* Instance Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-surface-200">
              Instância do Provider
            </label>
            {loadingInstances ? (
              <div className="flex items-center gap-2 text-surface-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Carregando...</span>
              </div>
            ) : (
              <select
                value={selectedInstanceId}
                onChange={(e) => {
                  setSelectedInstanceId(e.target.value);
                  setSelectedOperation('');
                  setResult(null);
                }}
                className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
              >
                <option value="">Selecione uma instância...</option>
                {instances.map((instance: ProviderInstance) => (
                  <option key={instance.id} value={instance.id}>
                    {instance.name} ({instance.template?.type || 'N/A'})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Operation Selection */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-surface-200">
              Operação
            </label>
            <select
              value={selectedOperation}
              onChange={(e) => setSelectedOperation(e.target.value)}
              disabled={!selectedInstanceId || operations.length === 0}
              className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500 disabled:opacity-50"
            >
              <option value="">Selecione uma operação...</option>
              {operations.map((op: Operation) => (
                <option key={op.name} value={op.name}>
                  {op.name} - {op.method} {op.endpoint}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Operation Info */}
        {currentOperation && (
          <div className="mt-4 p-3 bg-surface-800/50 rounded-lg border border-surface-700">
            <div className="flex items-center gap-2">
              <Badge variant={
                currentOperation.method === 'GET' ? 'success' :
                currentOperation.method === 'POST' ? 'primary' :
                currentOperation.method === 'PUT' ? 'warning' :
                currentOperation.method === 'DELETE' ? 'error' : 'info'
              }>
                {currentOperation.method}
              </Badge>
              <code className="text-sm text-surface-300 font-mono">
                {currentOperation.endpoint}
              </code>
            </div>
            {currentOperation.description && (
              <p className="text-xs text-surface-500 mt-2">{currentOperation.description}</p>
            )}
          </div>
        )}
      </Card>

      {/* Parameters */}
      {currentOperation?.parameters && currentOperation.parameters.length > 0 && (
        <Card className="p-4">
          <h3 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
            <FileJson className="w-4 h-4 text-primary-400" />
            Parâmetros
          </h3>
          
          <div className="space-y-4">
            {currentOperation.parameters.map((param: OperationParameter) => (
              <div key={param.name} className="space-y-1.5">
                <label className="block text-sm font-medium text-surface-300">
                  {param.name}
                  {param.required && <span className="text-red-400 ml-1">*</span>}
                  <span className="text-xs text-surface-500 ml-2">({param.type})</span>
                </label>
                
                {param.type === 'boolean' ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={parameters[param.name] || false}
                      onChange={(e) => handleParameterChange(param.name, e.target.checked)}
                      className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-500"
                    />
                    <span className="text-sm text-surface-300">
                      {param.description || 'Ativar'}
                    </span>
                  </label>
                ) : param.type === 'object' || param.type === 'array' ? (
                  <CodeEditor
                    value={typeof parameters[param.name] === 'string' 
                      ? parameters[param.name] 
                      : formatJson(parameters[param.name] || (param.type === 'array' ? [] : {}))}
                    onChange={(value) => {
                      try {
                        handleParameterChange(param.name, JSON.parse(value));
                      } catch {
                        handleParameterChange(param.name, value);
                      }
                    }}
                    mode="json"
                    minHeight="100px"
                    maxHeight="150px"
                    showToolbar={false}
                  />
                ) : (
                  <input
                    type={param.type === 'number' ? 'number' : 'text'}
                    value={parameters[param.name] || ''}
                    onChange={(e) => handleParameterChange(
                      param.name, 
                      param.type === 'number' ? Number(e.target.value) : e.target.value
                    )}
                    placeholder={param.description}
                    className="w-full px-4 py-2.5 bg-surface-800 border border-surface-600 rounded-lg text-white focus:outline-none focus:border-primary-500"
                  />
                )}
                
                {param.description && param.type !== 'boolean' && (
                  <p className="text-xs text-surface-500">{param.description}</p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Execute Buttons */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => handleExecute(true)}
          disabled={!selectedOperation || executeMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-surface-700 text-white rounded-lg font-medium hover:bg-surface-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {executeMutation.isPending && isDryRun ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Code className="w-4 h-4" />
          )}
          Dry Run
        </button>
        
        <button
          onClick={() => handleExecute(false)}
          disabled={!selectedOperation || executeMutation.isPending}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {executeMutation.isPending && !isDryRun ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Play className="w-4 h-4" />
          )}
          Executar
        </button>

        {result && (
          <div className="flex items-center gap-2 ml-auto">
            {result.duration && (
              <div className="flex items-center gap-1 text-xs text-surface-400">
                <Clock className="w-3 h-3" />
                {result.duration}ms
              </div>
            )}
            {result.statusCode && (
              <Badge variant={result.statusCode < 400 ? 'success' : 'error'}>
                {result.statusCode}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          {/* Status */}
          <div className={cn(
            'flex items-center gap-2 p-3 rounded-lg',
            result.success
              ? 'bg-green-500/10 border border-green-500/30'
              : 'bg-red-500/10 border border-red-500/30'
          )}>
            {result.success ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400" />
            )}
            <span className={cn(
              'font-medium',
              result.success ? 'text-green-400' : 'text-red-400'
            )}>
              {result.success ? 'Operação executada com sucesso' : 'Falha na execução'}
            </span>
          </div>

          {/* Request */}
          {result.request && (
            <Card className="overflow-hidden">
              <button
                onClick={() => setShowRequest(!showRequest)}
                className="w-full flex items-center justify-between p-3 bg-surface-800 hover:bg-surface-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {showRequest ? (
                    <ChevronDown className="w-4 h-4 text-surface-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-surface-400" />
                  )}
                  <span className="text-sm font-medium text-white">Request</span>
                  <Badge variant="info">{result.request.method}</Badge>
                </div>
                <code className="text-xs text-surface-400 font-mono truncate max-w-md">
                  {result.request.url}
                </code>
              </button>
              
              {showRequest && (
                <div className="p-4 space-y-3">
                  {/* Headers */}
                  <div>
                    <h4 className="text-xs font-medium text-surface-400 mb-2">Headers</h4>
                    <pre className="text-xs text-surface-300 bg-surface-900 p-3 rounded-lg overflow-x-auto">
                      {formatJson(result.request.headers)}
                    </pre>
                  </div>
                  
                  {/* Body */}
                  {result.request.body && (
                    <div>
                      <h4 className="text-xs font-medium text-surface-400 mb-2">Body</h4>
                      <pre className="text-xs text-surface-300 bg-surface-900 p-3 rounded-lg overflow-x-auto">
                        {formatJson(result.request.body)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}

          {/* Response */}
          {result.response && (
            <Card className="overflow-hidden">
              <button
                onClick={() => setShowResponse(!showResponse)}
                className="w-full flex items-center justify-between p-3 bg-surface-800 hover:bg-surface-700 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {showResponse ? (
                    <ChevronDown className="w-4 h-4 text-surface-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-surface-400" />
                  )}
                  <span className="text-sm font-medium text-white">Response</span>
                </div>
              </button>
              
              {showResponse && (
                <div className="p-4 space-y-3">
                  {/* Headers */}
                  <div>
                    <h4 className="text-xs font-medium text-surface-400 mb-2">Headers</h4>
                    <pre className="text-xs text-surface-300 bg-surface-900 p-3 rounded-lg overflow-x-auto">
                      {formatJson(result.response.headers)}
                    </pre>
                  </div>
                  
                  {/* Body */}
                  <div>
                    <h4 className="text-xs font-medium text-surface-400 mb-2">Body</h4>
                    <pre className="text-xs text-surface-300 bg-surface-900 p-3 rounded-lg overflow-x-auto max-h-96">
                      {formatJson(result.response.body)}
                    </pre>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Error */}
          {result.error && (
            <Card className="p-4 bg-red-500/5 border-red-500/30">
              <h4 className="text-sm font-medium text-red-400 mb-2">Erro</h4>
              <p className="text-sm text-surface-300">{result.error.message}</p>
              {result.error.code && (
                <p className="text-xs text-surface-500 mt-1">Código: {result.error.code}</p>
              )}
              {result.error.details && (
                <pre className="text-xs text-surface-400 bg-surface-900 p-3 rounded-lg mt-3 overflow-x-auto">
                  {formatJson(result.error.details)}
                </pre>
              )}
            </Card>
          )}

          {/* cURL Command */}
          {result.curlCommand && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-white flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-primary-400" />
                  cURL
                </h4>
                <button
                  onClick={() => copyToClipboard(result.curlCommand!)}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-surface-400 hover:text-white bg-surface-700 rounded transition-colors"
                >
                  <Copy className="w-3 h-3" />
                  Copiar
                </button>
              </div>
              <pre className="text-xs text-surface-300 bg-surface-900 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                {result.curlCommand}
              </pre>
            </Card>
          )}
        </div>
      )}

      {/* Empty State */}
      {!selectedOperation && (
        <Card className="p-8 text-center">
          <Zap className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <p className="text-surface-400 mb-2">Selecione uma instância e operação</p>
          <p className="text-xs text-surface-500">
            O console de testes permite executar operações em modo sandbox
          </p>
        </Card>
      )}
    </div>
  );
};

export default TestConsole;
